import { z } from 'zod'

const UserRoleSchema = z.object({
  id: z.string(),
  roleId: z.string(),
  name: z.string(),
  title: z.string(),
})

export const userProfilePictureSchema = z.object({
  url: z.string(),
  key: z.string(),
  expiresOn: z.string(),
})

export const userAddressSchema = z.object({
  id: z.string(),
  userId: z.string().optional(),
  street: z.string(),
  city: z.string(),
  state: z.string(),
  country: z.string(),
  zipCode: z.string(),
  createdAt: z.string(),
})

export const UserProfileSchema = z.object({
  id: z.string(),
  username: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phoneNumber: z.string(),
  address: userAddressSchema,
  profilePicture: userProfilePictureSchema,
  isAccountSetupCompleted: z.boolean(),
  lastActiveAt: z.string(),
  userRoles: z.array(UserRoleSchema),
  gender: z.string(),
  birthDate: z.string(),
  isChatAdmin: z.boolean().optional(),
  chatAdminId: z.string().optional(),
})

export type User = z.infer<typeof UserProfileSchema>
export type UserRole = z.infer<typeof UserRoleSchema>

export const DEFAULT_USER: User = {
  id: '',
  username: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  address: {
    id: '',
    street: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    createdAt: '',
  },
  profilePicture: { url: '', key: '', expiresOn: '' },
  isAccountSetupCompleted: false,
  lastActiveAt: '',
  userRoles: [],
  gender: '',
  birthDate: '',
}

export const userSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phoneNumber: z.string(),
  status: z.enum(['active', 'suspended', 'banned']),
  lastActiveAt: z.string().nullable(),
  role: z.string(),
  userRoles: z.array(
    z.object({
      id: z.string(),
      roleId: z.string(),
      name: z.string(),
      title: z.string(),
    })
  ),
  createdAt: z.string(),
  updatedAt: z.string(),
  suspendedUntil: z.string().nullable(),
  suspensionReason: z.string().nullable(),
  adminNotes: z.string().nullable(),
  events: z.number(),
  lastActive: z.string(),
  username: z.string().nullable(),
  gender: z.string(),
  birthDate: z.date(),
  profilePicture: userProfilePictureSchema,
  address: userAddressSchema,
  isChatAdmin: z.boolean().optional(),
  chatAdminId: z.string().optional(),
})

export const displayUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
  subscriptionType: z.string(),
  events: z.number(),
  lastActive: z.string(),
  status: z.enum(['Active', 'Suspended', 'Banned']),
})

export type ApiUserType = z.infer<typeof userSchema>
export type DisplayUser = z.infer<typeof userSchema>
export type UserResponse = {
  profilePicture: {
    url: string
    expiresOn: string
    key: string
  }
  address: {
    street: string
    city: string
    state: string
    country: string
    zipCode: string
  }
  username: string
} & ApiUserType

export type Activity = {
  id: string
  title: string
  createdAt: string
}

export const UserActivitySchema = z.object({
  id: z.string(),
  title: z.string(),
  createdAt: z.string(),
})

export type UserActivity = z.infer<typeof UserActivitySchema>

export const UserAttendedEventSchema = z.object({
  id: z.string(),
  status: z.string(),
  createdAt: z.string(),
  event: z.object({
    id: z.string(),
    name: z.string(),
    startDateTime: z.string(),
    endDateTime: z.string(),
    status: z.string(),
    type: z.string(),
  }),
  totalAttendees: z.number(),
})

export const DisplayUserAttendedEventSchema = z.object({
  id: z.string(),
  eventName: z.string(),
  startDateTime: z.string(),
  status: z.string(),
  totalAttendees: z.number(),
  type: z.string(),
})

export type UserAttendedEvent = z.infer<typeof UserAttendedEventSchema>
export type DisplayUserAttendedEvent = z.infer<
  typeof DisplayUserAttendedEventSchema
>
